import React from 'react';
import { cn } from '@/utils/cn';
import type {
  MetaFacebookPosition,
  MetaInstagramPosition,
  MetaPublisherPlatform,
} from '@/services/api/metaAds';

interface PlacementsSectionProps {
  publisherPlatforms: MetaPublisherPlatform[];
  facebookPositions: MetaFacebookPosition[];
  instagramPositions: MetaInstagramPosition[];
  /** A4.3.2.5: explicit Advantage+ Placements opt-in. When true, the
   *  manual UI collapses and the create call omits publisher_platforms /
   *  facebook_positions / instagram_positions (Meta auto-selects). */
  advantagePlacements: boolean;
  onChangePublisherPlatforms: (platforms: MetaPublisherPlatform[]) => void;
  onChangeFacebookPositions: (positions: MetaFacebookPosition[]) => void;
  onChangeInstagramPositions: (positions: MetaInstagramPosition[]) => void;
  /** Mode switch is a SINGLE callback (not four field setters in a row)
   *  because the consumer's per-field state setters all capture the
   *  same closure'd targeting; four sequential calls would race and the
   *  last patch would win. The parent applies the multi-field patch
   *  atomically. */
  onSetPlacementMode: (mode: 'advantage' | 'manual') => void;
  error?: string;
}

const PUBLISHER_PLATFORM_LABELS: Record<MetaPublisherPlatform, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  audience_network: 'Audience Network',
  messenger: 'Messenger',
};

const FACEBOOK_POSITION_LABELS: Record<MetaFacebookPosition, string> = {
  feed: 'Feeds',
  right_hand_column: 'Right column',
  marketplace: 'Marketplace',
  video_feeds: 'Video feeds',
  story: 'Stories',
  search: 'Search results',
  instream_video: 'In-stream video',
  facebook_reels: 'Reels',
  facebook_reels_overlay: 'Reels overlay',
  biz_disco_feed: 'Business discovery',
  profile_feed: 'Profile feed',
};

const INSTAGRAM_POSITION_LABELS: Record<MetaInstagramPosition, string> = {
  stream: 'Feed',
  story: 'Stories',
  explore: 'Explore',
  reels: 'Reels',
  ig_search: 'Search',
  profile_feed: 'Profile feed',
  explore_home: 'Explore home',
};

/**
 * Placement selection — Advantage+ vs Manual. Advantage+ Placements
 * (A4.3.2.5) sets `advantage_placements=true` AND clears the manual
 * fields; backend serializer omits the placement keys so Meta picks.
 * Manual reveals platform checkboxes; each enabled platform shows its
 * position sub-checkboxes. The backend validator rejects mixing the
 * two, so the radio is genuinely exclusive.
 */
export const PlacementsSection: React.FC<PlacementsSectionProps> = ({
  publisherPlatforms,
  facebookPositions,
  instagramPositions,
  advantagePlacements,
  onChangePublisherPlatforms,
  onChangeFacebookPositions,
  onChangeInstagramPositions,
  onSetPlacementMode,
  error,
}) => {
  const togglePlatform = (p: MetaPublisherPlatform) => {
    if (publisherPlatforms.includes(p)) {
      const next = publisherPlatforms.filter((x) => x !== p);
      if (next.length === 0) {
        // Removing the last platform in Manual mode would leave the
        // state inconsistent (`advantage_placements` stays `false` but
        // the wire still produces Advantage+ via the empty list, since
        // Meta interprets omitted placement keys as automatic). Flip
        // to Advantage mode atomically so what the UI shows matches
        // what Meta will receive. Same anti-drift reasoning as the
        // default in `CreateAdSetWizard.tsx defaultTargeting()`.
        onSetPlacementMode('advantage');
        return;
      }
      onChangePublisherPlatforms(next);
      // Clear positions for the disabled platform — model validator
      // rejects `facebook_positions` without `facebook` in
      // publisher_platforms (and vice versa). Two sequential calls
      // here are safe because TargetingStep's `update()` uses the
      // functional setter form: each call sees the previous call's
      // effect. (Earlier the parent spread a closure-captured
      // `targeting` prop, which made the second call clobber the
      // first — see TargetingStep.tsx for the fix.)
      if (p === 'facebook') onChangeFacebookPositions([]);
      if (p === 'instagram') onChangeInstagramPositions([]);
    } else {
      onChangePublisherPlatforms([...publisherPlatforms, p]);
    }
  };

  const setAdvantage = () => onSetPlacementMode('advantage');
  const setManual = () => onSetPlacementMode('manual');

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm text-gray-600 mb-2">
          Advantage+ Placements lets Meta pick where to show ads for the
          best performance per dollar. Manual placements restrict to
          specific platforms and positions.
        </p>
      </div>

      <div role="radiogroup" aria-label="Placement mode" className="flex gap-2">
        <button
          type="button"
          role="radio"
          aria-checked={advantagePlacements}
          onClick={setAdvantage}
          className={cn(
            'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
            advantagePlacements
              ? 'border-blue-600 bg-blue-50 text-blue-900'
              : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
          )}
        >
          Advantage+ Placements
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={!advantagePlacements}
          onClick={setManual}
          className={cn(
            'flex-1 p-3 border-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
            !advantagePlacements
              ? 'border-blue-600 bg-blue-50 text-blue-900'
              : 'border-gray-200 hover:bg-gray-50 bg-white text-gray-700',
          )}
        >
          Manual
        </button>
      </div>

      {!advantagePlacements && (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-700 mb-2">Platforms</p>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(PUBLISHER_PLATFORM_LABELS) as MetaPublisherPlatform[]).map((p) => {
                const selected = publisherPlatforms.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    aria-pressed={selected}
                    className={cn(
                      'text-xs px-3 py-1.5 border rounded',
                      selected
                        ? 'bg-blue-50 border-blue-400 text-blue-900'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                    )}
                  >
                    {PUBLISHER_PLATFORM_LABELS[p]}
                  </button>
                );
              })}
            </div>
          </div>

          {publisherPlatforms.includes('facebook') && (
            <PositionGroup
              title="Facebook positions"
              hint="Leave all blank to deliver to all Facebook positions."
              options={Object.keys(FACEBOOK_POSITION_LABELS) as MetaFacebookPosition[]}
              labels={FACEBOOK_POSITION_LABELS}
              selected={facebookPositions}
              onChange={onChangeFacebookPositions}
            />
          )}

          {publisherPlatforms.includes('instagram') && (
            <PositionGroup
              title="Instagram positions"
              hint="Leave all blank to deliver to all Instagram positions."
              options={Object.keys(INSTAGRAM_POSITION_LABELS) as MetaInstagramPosition[]}
              labels={INSTAGRAM_POSITION_LABELS}
              selected={instagramPositions}
              onChange={onChangeInstagramPositions}
            />
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

interface PositionGroupProps<T extends string> {
  title: string;
  hint: string;
  options: T[];
  labels: Record<T, string>;
  selected: T[];
  onChange: (selected: T[]) => void;
}

function PositionGroup<T extends string>({
  title,
  hint,
  options,
  labels,
  selected,
  onChange,
}: PositionGroupProps<T>): React.ReactElement {
  const toggle = (opt: T) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((x) => x !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };

  return (
    <div>
      <p className="text-xs font-medium text-gray-700 mb-1">{title}</p>
      <p className="text-xs text-gray-500 mb-2">{hint}</p>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => {
          const checked = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              aria-pressed={checked}
              className={cn(
                'text-xs px-2 py-1 border rounded',
                checked
                  ? 'bg-blue-50 border-blue-400 text-blue-900'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
              )}
            >
              {labels[opt]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
